import React, { useEffect, useRef } from "react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__pqTurnstileReady&render=explicit";
const SCRIPT_ID = "cf-turnstile-script";

let scriptPromise = null;

/** Load the Turnstile script exactly once per page. */
function loadTurnstile() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    window.__pqTurnstileReady = () => resolve(window.turnstile);
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) return;
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error("Failed to load Turnstile"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * TurnstileWidget — renders a Cloudflare Turnstile challenge.
 * Props:
 *  - siteKey (string, required)
 *  - onVerify(token): called when the user solves the challenge
 *  - onExpire(): called when the token expires (we clear it on the parent)
 *  - onError(): called on widget errors
 *  - theme: "dark" | "light" | "auto" (default "dark")
 */
export default function TurnstileWidget({ siteKey, onVerify, onExpire, onError, theme = "dark" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) return;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !containerRef.current || !ts) return;
        widgetIdRef.current = ts.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => onVerify && onVerify(token),
          "expired-callback": () => onExpire && onExpire(),
          "error-callback": () => onError && onError(),
        });
      })
      .catch(() => onError && onError());
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
    // Only re-run when siteKey changes. Callbacks are intentionally omitted —
    // Turnstile's `render` is one-shot and we don't want to tear it down just
    // because a parent re-renders with a fresh closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  return <div ref={containerRef} data-testid="turnstile-widget" />;
}
